import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useCustomer } from "@/context/CustomerContext";
import { inputCls, btnPrimary } from "@/lib/labels";
import { AuthShell, FormError } from "@/components/account/AuthShell";
import { PasswordInput, Field } from "@/components/account/PasswordInput";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/customer/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="نسيت كلمة السر؟" subtitle="اكتب إيميل حسابك وهنبعتلك لينك تختار بيه كلمة سر جديدة." testId="forgot-password-page">
      {sent ? (
        <div className="space-y-4 text-center" data-testid="forgot-password-sent">
          <MailCheck className="mx-auto h-14 w-14 text-[var(--km-cyan)]" />
          <p className="text-white">لو الإيميل ده متسجل عندنا، هيوصلك لينك إعادة التعيين خلال دقايق. اللينك صالح لمدة ساعة.</p>
          <Link to="/login" className="text-[var(--km-lilac)] underline">ارجع لتسجيل الدخول</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
          <Field label="الإيميل">
            <input type="email" dir="ltr" className={`${inputCls} text-right`} value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input" />
          </Field>
          <FormError msg={error} />
          <button type="submit" disabled={busy} className={`${btnPrimary} w-full`} data-testid="forgot-submit-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} ابعت اللينك
          </button>
        </form>
      )}
    </AuthShell>
  );
}

export function ChangeTempPassword() {
  const { setCustomer } = useCustomer();
  const navigate = useNavigate();
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (f.next.length < 8) return setError("كلمة السر لازم تكون 8 حروف على الأقل");
    if (f.next !== f.confirm) return setError("تأكيد كلمة السر مش مطابق");
    setBusy(true);
    try {
      setCustomer((await api.post("/customer/change-password", { current_password: f.current, new_password: f.next })).data);
      navigate("/account", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell title="غيّر كلمة السر" subtitle="الإدارة عملتلك كلمة سر مؤقتة. اختار كلمة سر جديدة عشان تكمل." testId="change-temp-password-page">
      <form onSubmit={submit} className="space-y-4" data-testid="change-temp-password-form">
        <Field label="كلمة السر المؤقتة">
          <PasswordInput value={f.current} onChange={set("current")} testId="temp-current-input" />
        </Field>
        <Field label="كلمة السر الجديدة" hint="8 حروف على الأقل">
          <PasswordInput value={f.next} onChange={set("next")} autoComplete="new-password" testId="temp-new-input" />
        </Field>
        <Field label="أكّد كلمة السر">
          <PasswordInput value={f.confirm} onChange={set("confirm")} autoComplete="new-password" testId="temp-confirm-input" />
        </Field>
        <FormError msg={error} />
        <button type="submit" disabled={busy} className={`${btnPrimary} w-full`} data-testid="temp-submit-btn">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} حفظ وكمل
        </button>
      </form>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("كلمة السر لازم تكون 8 حروف على الأقل");
    if (pw !== confirm) return setError("تأكيد كلمة السر مش مطابق");
    setBusy(true);
    try {
      await api.post("/customer/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="كلمة سر جديدة" testId="reset-password-page">
      {done ? (
        <div className="space-y-4 text-center" data-testid="reset-password-done">
          <p className="text-white">كلمة السر اتغيرت بنجاح.</p>
          <Link to="/login" className={btnPrimary} data-testid="reset-go-login">سجل دخول</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
          <Field label="كلمة السر الجديدة" hint="8 حروف على الأقل">
            <PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" testId="reset-password-input" />
          </Field>
          <Field label="أكّد كلمة السر">
            <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" testId="reset-confirm-input" />
          </Field>
          <FormError msg={!token ? "اللينك ناقص، اطلب لينك جديد" : error} />
          <button type="submit" disabled={busy || !token} className={`${btnPrimary} w-full`} data-testid="reset-submit-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} حفظ
          </button>
        </form>
      )}
    </AuthShell>
  );
}
